import React, { useState, useEffect } from 'react';
import { productAPI } from '../../services/api';

const ProductFilters = ({ onFilterChange, currentFilters = {} }) => {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: currentFilters.category || '',
    brand: currentFilters.brand || '',
    minPrice: currentFilters.minPrice || '',
    maxPrice: currentFilters.maxPrice || '',
    search: currentFilters.search || '',
    sortBy: currentFilters.sortBy || 'name',
    sortDirection: currentFilters.sortDirection || 'asc'
  });

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      setIsLoading(true);
      const [categoriesResponse, brandsResponse] = await Promise.all([
        productAPI.getCategories(),
        productAPI.getBrands()
      ]);
      
      setCategories(categoriesResponse.data || []);
      setBrands(brandsResponse.data || []);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      category: '',
      brand: '',
      minPrice: '',
      maxPrice: '',
      search: '',
      sortBy: 'name',
      sortDirection: 'asc'
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const filterStyle = {
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #dee2e6'
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#495057'
  };

  const rowStyle = {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    flexWrap: 'wrap'
  };

  const colStyle = {
    flex: 1,
    minWidth: '150px'
  };

  if (isLoading) {
    return <div style={filterStyle}>Loading filters...</div>;
  }

  return (
    <div style={filterStyle}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>
        Filter Products
      </h3>
      
      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Search</label>
        <input
          type="text"
          placeholder="Search products..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          style={inputStyle}
        />
      </div>

      {/* Category and Brand */}
      <div style={rowStyle}>
        <div style={colStyle}>
          <label style={labelStyle}>Category</label>
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            style={inputStyle}
          >
            <option value="">All Categories</option>
            {categories.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        
        <div style={colStyle}>
          <label style={labelStyle}>Brand</label>
          <select
            value={filters.brand}
            onChange={(e) => handleFilterChange('brand', e.target.value)}
            style={inputStyle}
          >
            <option value="">All Brands</option>
            {brands.map((brand, index) => (
              <option key={index} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Price Range */}
      <div style={rowStyle}>
        <div style={colStyle}>
          <label style={labelStyle}>Min Price</label>
          <input
            type="number"
            placeholder="0"
            min="0"
            step="0.01"
            value={filters.minPrice}
            onChange={(e) => handleFilterChange('minPrice', e.target.value)}
            style={inputStyle}
          />
        </div>
        
        <div style={colStyle}>
          <label style={labelStyle}>Max Price</label>
          <input
            type="number"
            placeholder="No limit"
            min="0"
            step="0.01"
            value={filters.maxPrice}
            onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>

      {/* Sort Options */}
      <div style={rowStyle}>
        <div style={colStyle}>
          <label style={labelStyle}>Sort By</label>
          <select
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            style={inputStyle}
          >
            <option value="name">Name</option>
            <option value="price">Price</option>
            <option value="createdAt">Date Added</option>
          </select>
        </div>
        
        <div style={colStyle}>
          <label style={labelStyle}>Sort Direction</label>
          <select
            value={filters.sortDirection}
            onChange={(e) => handleFilterChange('sortDirection', e.target.value)}
            style={inputStyle}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
      </div>

      {/* Clear Filters Button */}
      <button
        onClick={clearFilters}
        style={{
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#545b62'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
      >
        Clear All Filters
      </button>
    </div>
  );
};

export default ProductFilters;